import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Users, Phone, Mail, Calendar } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = ['new', 'contacted', 'converted', 'closed'];
const statusColors = {
  'new': 'bg-green-100 text-green-800',
  'contacted': 'bg-blue-100 text-blue-800',
  'converted': 'bg-purple-100 text-purple-800',
  'closed': 'bg-gray-100 text-gray-800'
};

export default function LeadsList() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (leadId, newStatus) => {
    setUpdating(leadId);
    try {
      await axios.put(`${API}/leads/${leadId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="leads-list">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Leads</h1>
        <p className="text-gray-500">Manage customer enquiries and leads</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
        </div>
      ) : leads.length > 0 ? (
        <div className="space-y-4">
          {leads.map((lead) => (
            <Card key={lead.id} data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-heading text-lg font-semibold text-gray-900">{lead.name}</h3>
                      <Badge className={statusColors[lead.status]}>{lead.status}</Badge>
                      <Badge variant="outline">{lead.source}</Badge>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-600 mb-3">
                      <a href={`tel:${lead.mobile}`} className="flex items-center hover:text-brand-orange">
                        <Phone className="h-4 w-4 mr-2" />
                        {lead.mobile}
                      </a>
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center hover:text-brand-orange">
                          <Mail className="h-4 w-4 mr-2" />
                          {lead.email}
                        </a>
                      )}
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        {formatDate(lead.created_at)}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-3">
                      {lead.property_type && (
                        <span className="text-xs bg-orange-50 text-brand-rust px-2 py-1 rounded">
                          Interest: {lead.property_type}
                        </span>
                      )}
                      {lead.preferred_location && (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          Location: {lead.preferred_location}
                        </span>
                      )}
                      {lead.property_title && (
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                          Property: {lead.property_title}
                        </span>
                      )}
                    </div>

                    {lead.message && (
                      <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
                        <strong>Message:</strong> {lead.message}
                      </div>
                    )}

                    {(lead.preferred_date || lead.preferred_time) && (
                      <div className="mt-2 text-sm text-gray-500">
                        Preferred visit: {lead.preferred_date} {lead.preferred_time}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-3">
                    <Select 
                      value={lead.status} 
                      onValueChange={(v) => updateStatus(lead.id, v)}
                      disabled={updating === lead.id}
                    >
                      <SelectTrigger className="w-36" data-testid={`status-select-${lead.id}`}>
                        {updating === lead.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <SelectValue />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <a href={`tel:${lead.mobile}`}>
                      <Button variant="outline" size="sm">
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-20">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No leads yet</p>
            <p className="text-sm text-gray-400 mt-2">
              Leads will appear here when customers submit enquiries
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
